/**
 * The Think Tank in HQ (build TT): the page's pure words and helpers, shared by the tabs.
 * Randy and Jeff set the rules; every next move is scored by them, with the evidence one click
 * away. Nothing here deletes anything: the bench keeps every opportunity, every rules change is a
 * new version.
 */

export type TabKey = "week" | "opportunities" | "rules" | "intel" | "desk" | "pilots";
export type RuleKey = "reach" | "revenue" | "reuse" | "moat" | "dog_first" | "risk";
export type Trust = "trusted" | "weak" | "blocked";

/** The six tabs, in the mockup's order. */
export const TABS: readonly (readonly [TabKey, string])[] = [
  ["week", "This week"],
  ["opportunities", "Opportunities"],
  ["rules", "Rules"],
  ["intel", "Intel"],
  ["desk", "Research desk"],
  ["pilots", "Pilots"],
];

export const TITLE = "Think Tank";
export const SUBTITLE =
  "What the market is doing, what our people are telling us, and what to do next. Randy and Jeff set the rules.";
export const SCORECARD_LINE = "Every possible next move, scored by your rules";
export const SCORECARD_HELP =
  "Click any score to see the evidence behind it. Scores update weekly; the “Randy’s #” tag shows where his order and the scores differ.";
export const RULES_HELP =
  "Drag to change. Every change is saved as a new version, so you can see how the ranking would have looked.";
export const INSTINCTS_HELP =
  "Your order, kept next to the scores. When they disagree by 2+ places, the scorecard explains why.";
export const PACK_TITLE = "Heard from the Pack";
export const WATCH_TITLE = "Who we watch";

export const TRUST_LABEL: Record<Trust, string> = { trusted: "Trusted", weak: "Weak", blocked: "Blocked" };
export const SOURCE_KINDS: readonly (readonly [string, string])[] = [
  ["company", "Company site"],
  ["filing", "Filing"],
  ["trade_press", "Trade press"],
  ["app_store", "App store"],
  ["pack", "Our own Pack"],
  ["forum", "Forum"],
  ["review_site", "Review site"],
  ["other", "Other"],
];
export const INTEL_KINDS: readonly (readonly [string, string])[] = [
  ["launch", "Launch"],
  ["price", "Price"],
  ["review", "Reviews"],
  ["hiring", "Hiring"],
  ["funding", "Funding"],
  ["partnership", "Partnership"],
  ["other", "News"],
];
export const CONFIDENCE: readonly string[] = ["high", "medium", "low"];

export function total(weights: Record<string, number>): number {
  return Object.values(weights).reduce((a, b) => a + b, 0);
}

/** The weights line under the sliders: OK at 100, else how far off. */
export function weightsLine(weights: Record<string, number>): { ok: boolean; line: string } {
  const t = total(weights);
  if (t === 100) return { ok: true, line: "Adds up to 100%" };
  return { ok: false, line: t > 100 ? `${t}% · take off ${t - 100}%` : `${t}% · add ${100 - t}%` };
}

/** Move one item in an ordered list (Randy's instincts). */
export function move<T>(list: readonly T[], from: number, by: -1 | 1): T[] {
  const to = from + by;
  if (to < 0 || to >= list.length) return [...list];
  const next = [...list];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

export function trendClass(label: string): string {
  if (label.startsWith("▲")) return "text-emerald-700";
  if (label.startsWith("▼")) return "text-red-700";
  return "text-zinc-400";
}

export function riskClass(label: string): string {
  return label === "high" ? "text-red-700" : label === "med" ? "text-amber-700" : "text-zinc-700";
}

/** Dates as the timelines say them: "Oct 23, 2026". */
export function dateWords(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1];
  return `${month} ${d}, ${y}`;
}

/** Empty strings from a form go to the API as null. */
export function orNull(value: string): string | null {
  const v = value.trim();
  return v ? v : null;
}

/** The API's error body is { error: { message } }; say its words, or a plain fallback. */
export function errorMessage(error: unknown, fallback: string): string {
  const e = error as { error?: { message?: string; details?: { errors?: { msg?: string }[] } } } | null;
  const detail = e?.error?.details?.errors?.[0]?.msg?.replace(/^Value error, /, "");
  return detail || e?.error?.message || fallback;
}

// --- TT2: This week, the Research desk, Pilots ---------------------------------------------

export type Verdict = "great" | "missing" | "wrong";
export const VERDICTS: readonly (readonly [Verdict, string])[] = [["great", "Great"], ["missing", "Missing something"], ["wrong", "Wrong"]];
export const DECISIONS: readonly (readonly ["pilot" | "deeper" | "not_now", string])[] = [
  ["pilot", "Start a pilot"], ["deeper", "Ask a deeper question"], ["not_now", "Not now"],
];
export const ITEM_TONE: Record<string, string> = {
  opportunity: "border-emerald-200 bg-emerald-50/40",
  market: "border-sky-200 bg-sky-50/40",
  pack: "border-violet-200 bg-violet-50/40",
};
export const DESK_STATUS: Record<string, string> = { queued: "text-zinc-500", researching: "text-amber-800", done: "text-emerald-800" };
export const VERDICT_TONE: Record<string, string> = { nailed: "text-emerald-800", okay: "text-amber-800", missed: "text-red-700" };

const FILLER = /^(?:it\s+)?(?:needs?|need to|should|please|also|and|add|include|get|we need|missing)\s+(?:the\s+|an?\s+|some\s+|more\s+)?(?:actual\s+|real\s+)?/i;

/** Checklist items from a rating's note (the same plain rules as the server's
 * `think_tank_desk.suggest_items`); the rater edits them before saving. */
export function suggestItems(note: string | null | undefined): string[] {
  if (!note) return [];
  const parts = note.split(/[;.\n]|,\s*(?:and\s+)?|\s+and\s+(?=talk|ask|check|add|get|look|compare)/);
  const out: string[] = [];
  for (const raw of parts) {
    const p = raw.trim().replace(FILLER, "").trim().replace(/^[\s,.;:-]+|[\s,.;:-]+$/g, "").trim();
    if (p.length < 2 || p.length > 120 || out.some(x => x.toLowerCase() === p.toLowerCase())) continue;
    out.push(p.slice(0, 2) === p.slice(0, 2).toUpperCase() ? p : p[0].toLowerCase() + p.slice(1));
  }
  return out.slice(0, 5);
}
